import { html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement } from 'lit/decorators.js';
import * as TagNames from './segmented-control.tagnames';
import { AtlSegmentedControlButtonProps } from './segmented-control.props';
import { getGlobalStyleSheets } from '../../global-styles';
import { ButtonEnums } from '../button';
import { IconEnums } from '../icon';
import { AtlSegmentedControlButtonClickEvent } from './segmented-control.events';
import { classMap } from 'lit/directives/class-map.js';
import { AtlSegmentedControl } from './segmented-control.component';
import { AtlIconTemplate } from '../icon/icon.component';

import SegmentedControlStyles from './segmented-control.scss?inline';
import { ifDefined } from 'lit/directives/if-defined.js';

type ITooltip = {
   dispose: () => void;
   update: () => void;
   hide: () => void;

   _config: {
      title: string;
      placement: string;
   };
};

@customElement(TagNames.SegmentedControlButton)
export class AtlSegmentedControlButton
   extends LitElement
   implements AtlSegmentedControlButtonProps
{
   declare value?: string;
   declare label?: string;
   declare active?: boolean;
   declare iconName?: string;
   declare showDefaultIcon?: boolean;
   declare defaultActive?: boolean;
   declare _isFirstButton?: boolean;
   declare _isLastButton?: boolean;
   declare _enableTooltip?: boolean;

   private _buttonTooltip: ITooltip | undefined;

   static properties = {
      active: { type: Boolean },
      label: { type: String },
      iconName: { attribute: 'icon-name', type: String },
      showDefaultIcon: { type: Boolean },
      defaultActive: { type: Boolean },
      value: { type: String },
      _isFirstButton: { type: Boolean },
      _isLastButton: { type: Boolean },
      _enableTooltip: { type: Boolean },
   };

   static styles = [...getGlobalStyleSheets(), SegmentedControlStyles];

   firstUpdated() {
      this._handleTooltip();
   }

   protected override updated(changedProperties: PropertyValues) {
      super.updated(changedProperties);
      this._handleTooltip();
   }

   private _handleTooltip() {
      if (!this.label) return;

      const parent = this.closest(
         TagNames.SegmentedControl
      ) as AtlSegmentedControl;
      if (!parent?.hideLabels) {
         this._buttonTooltip?.dispose();
         return;
      }

      const button = this.shadowRoot?.querySelector('label') as HTMLElement;
      if (button) {
         this._buttonTooltip = new bootstrap.Tooltip(button, {
            boundary: button,
            trigger: 'hover',
            title: this.label,
            placement: 'top',
            offset: '0, 6',
         });
      }
   }

   private handleClick = () => {
      this.dispatchEvent(new AtlSegmentedControlButtonClickEvent());
      this._buttonTooltip?.hide();
   };

   render() {
      const parent = this.closest(
         TagNames.SegmentedControl
      ) as AtlSegmentedControl;

      const buttonClassNames = {
         btn: true,
         'btn-secondary': true,
         'segmented-control-button': true,
         'btn-sm': parent?.size === ButtonEnums.ButtonSize.Small,
         'btn-md': parent?.size === ButtonEnums.ButtonSize.Medium,
         'btn-lg': parent?.size === ButtonEnums.ButtonSize.Large,
         'first-button': this._isFirstButton === true,
         'last-button': this._isLastButton === true,
         'evenly-spread': parent?.evenlySpread == true,
      };

      const iconName = this.iconName
         ? this.iconName
         : this.showDefaultIcon && this.active
           ? 'check'
           : '';
      const iconHiddenClass = iconName ? '' : 'visually-hidden';

      return html`
         <input
            type="radio"
            name=${parent?.name || ''}
            class="btn-check"
            autocomplete="off"
            value=${ifDefined(this.value)}
            title=${this.label || ''}
            aria-label=${this.label || 'input'}
            ?checked=${this.active}
            @change=${this.handleClick}
         />
         <label
            aria-label=${this.label || 'label'}
            data-bs-toggle="${parent?.hideLabels ? 'tooltip' : nothing}"
            class=${classMap(buttonClassNames)}
            part="label"
            @click=${this.handleClick}
         >
            ${iconName
               ? html`${AtlIconTemplate({
                    iconName: iconName,
                    iconSize: IconEnums.IconSizes.ExtraSmall,
                    iconSource: IconEnums.IconSource.FontAwesome,
                    iconClassNames: iconHiddenClass,
                    ariaHidden: true,
                 })}`
               : nothing}
            ${parent?.hideLabels === true
               ? nothing
               : html`<span class="label">${this.label}</span>`}
         </label>
      `;
   }
}
